import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  InterviewSlotSerialized,
  InterviewSlotsState
} from 'types/interviewSlots';

const initialState: InterviewSlotsState = {
  slots: [],
  proposedSlots: [],
  selectedSlot: null,
  applicationId: null,
  isLoading: false,
  error: null
};

const interviewSlotsSlice = createSlice({
  name: 'interviewSlots',
  initialState,
  reducers: {
    addSlot: (state, action: PayloadAction<InterviewSlotSerialized>) => {
      state.slots.push(action.payload);
      state.error = null;
    },
    removeSlot: (state, action: PayloadAction<number>) => {
      state.slots = state.slots.filter((slot) => slot.id !== action.payload);
    },
    clearSlots: (state) => {
      state.slots = [];
    },
    setProposedSlots: (
      state,
      action: PayloadAction<InterviewSlotSerialized[]>
    ) => {
      const newProposedSlots = action.payload;
      state.proposedSlots = newProposedSlots;

      if (state.selectedSlot) {
        const stillExists = newProposedSlots.some(
          (slot) => slot.id === state.selectedSlot?.id
        );
        if (!stillExists) {
          state.selectedSlot = null;
        }
      }

      state.error = null;
    },
    initProposedSlots: (
      state,
      action: PayloadAction<InterviewSlotSerialized[]>
    ) => {
      const newSlots = action.payload;
      const slotsChanged =
        state.proposedSlots.length !== newSlots.length ||
        !newSlots.every((slot) =>
          state.proposedSlots.some(
            (existingSlot) => existingSlot.id === slot.id
          )
        );

      if (slotsChanged) {
        state.proposedSlots = newSlots;
      }

      state.error = null;
    },
    selectProposedSlot: (
      state,
      action: PayloadAction<InterviewSlotSerialized>
    ) => {
      const slot = action.payload;
      if (state.selectedSlot?.id === slot.id) {
        state.selectedSlot = null;
      } else {
        state.selectedSlot = slot;
      }
    },
    clearProposedSlots: (state) => {
      state.proposedSlots = [];
      state.selectedSlot = null;
    },
    setApplicationId: (state, action: PayloadAction<string | null>) => {
      if (state.applicationId !== action.payload) {
        state.slots = [];
        state.proposedSlots = [];
        state.selectedSlot = null;
        state.applicationId = action.payload;
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    resetSlots: (state) => {
      state.slots = [];
      state.proposedSlots = [];
      state.selectedSlot = null;
      state.applicationId = null;
      state.error = null;
    }
  }
});

export const {
  addSlot,
  removeSlot,
  clearSlots,
  setProposedSlots,
  initProposedSlots,
  selectProposedSlot,
  clearProposedSlots,
  setApplicationId,
  setLoading,
  setError,
  resetSlots
} = interviewSlotsSlice.actions;

export default interviewSlotsSlice.reducer;
